import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export const LEVELS = [
  { id: "founder", label: "Founders & owners" },
  { id: "c_suite", label: "C-suite" },
  { id: "partner", label: "Partners" },
  { id: "vp", label: "VP / SVP" },
  { id: "director", label: "Directors & heads" },
  { id: "manager", label: "Managers" },
  { id: "ic", label: "Individual contributors" },
  { id: null, label: "Other" },
];

const LEVEL_WORDS = [
  ["founder", /\b(founder|co-founder|owner|proprietor)\b/i],
  ["vp", /\b(vp|svp|evp|vice president|vice-president)\b/i],
  ["c_suite", /\b(chief|ceo|cio|cto|cfo|coo|cmo|cro|ciso|cdo|cpo|chro|president|principal|managing director)\b/i],
  ["partner", /\bpartner\b/i],
  ["director", /\b(director|head of|head,)\b/i],
  ["manager", /\b(manager|lead|supervisor)\b/i],
  ["ic", /\b(engineer|analyst|associate|specialist|coordinator|assistant|intern|consultant|advisor|representative)\b/i],
];

export const levelOf = (person) => person.level ?? (LEVEL_WORDS.find(([, re]) => re.test(person.personTitle || ""))?.[0] ?? null);

// Primary (the hunt prospect) + the folded "also at" buyers, grouped by seniority for the org-map.
export const committeeGroups = (lead) => {
  const primary = lead.personName ? { ...lead, isPrimary: true, level: levelOf(lead) } : null;
  const others = (lead.alsoAt || []).map((p) => ({ ...p, level: levelOf(p) }));
  const people = [...(primary ? [primary] : []), ...others];
  return LEVELS.map((lvl) => ({ ...lvl, people: people.filter((p) => p.level === lvl.id) })).filter((g) => g.people.length);
};

export const useBuyingCommittee = ({ lead, userId, onAdded }) => {
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const others = lead.alsoAt || [];
  const done = new Set([...(result?.contacts || []), ...(result?.skipped || [])].map((x) => x.personName));

  const toggle = (name) => { if (done.has(name)) return; setPicked((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; }); };
  const pickAll = () => setPicked(new Set(others.filter((p) => !done.has(p.personName)).map((p) => p.personName)));

  const add = async () => {
    const buyers = others.filter((p) => picked.has(p.personName) && !done.has(p.personName)).map(({ personName, personTitle, url, level }) => ({ personName, personTitle, url, level }));
    if (!buyers.length) return toast.error("Pick at least one buyer to add");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/leads/committee/${userId}`, {
        companyName: lead.companyName, companyDomain: lead.companyDomain || null, website: lead.companyDomain ? `https://${lead.companyDomain}` : null,
        industry: lead.companyIndustry || null, companySize: lead.employeeCount ? `${Number(lead.employeeCount).toLocaleString()} employees` : null,
        primaryContactName: lead.personName || null, buyers, lookupEmail: true,
      });
      setResult((prev) => ({ ...data, contacts: [...(prev?.contacts || []), ...data.contacts], skipped: [...(prev?.skipped || []), ...(data.skipped || [])] }));
      setPicked(new Set());
      const n = data.contacts.length;
      if (n) toast.success(`${n} buyer${n > 1 ? "s" : ""} added to ${lead.companyName} · ${data.emailsFound ? `${data.emailsFound} verified email${data.emailsFound > 1 ? "s" : ""} found` : "no verified email found yet"}`);
      (data.skipped || []).forEach((s) => toast.warning(`${s.personName} not added — ${s.reason}`));
      onAdded?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the buyers");
    } finally {
      setBusy(false);
    }
  };

  return { picked, toggle, pickAll, add, busy, result, groups: committeeGroups(lead), others, done };
};
